import { isRecord } from '@atlassian/conversation-assistant-utils/isRecord';

// Referenced from jira/src/packages/platform/utils/fetch/src/utils/is-error.tsx
// Issues we are not gonna track in Sentry
const KNOWN_NETWORK_ERRORS_LIMITED = [
	'server with the specified hostname could not be found',
	'connection failure',
	'Failed to fetch',
	'Load failed', // Safari equivalent of "Failed to fetch"
	'Network request failed',
	'NetworkError',
	'The request timed out',
	'The network connection was lost',
	'The operation was aborted',
];

const isClientFetchErrorMessage = (message: string): boolean => {
	const errorTypes = KNOWN_NETWORK_ERRORS_LIMITED;
	return (
		Boolean(message) &&
		!!errorTypes.find((networkErrorMessage) =>
			// Using `includes` here because the underlying fetch error messages
			// are sometimes wrapped to provide additional context, e.g.
			// "Something went wrong: ${error.message}"
			message.includes(networkErrorMessage),
		)
	);
};

const KNOWN_NETWORK_ERRORS_TYPES = ['AbortError', 'AuthError', 'ChunkLoadError', 'NetworkError'];

export const isClientFetchError = (error: unknown): boolean => {
	if (!isRecord(error)) {
		return false;
	}

	// Check top-level error name and message
	const hasKnownName =
		typeof error.name === 'string' && KNOWN_NETWORK_ERRORS_TYPES.includes(error.name);
	const hasKnownMessage =
		typeof error.message === 'string' && isClientFetchErrorMessage(error.message);
	if (hasKnownName || hasKnownMessage) {
		return true;
	}

	// Also check the nested cause (e.g. wrapped fetch errors)
	if (!isRecord(error.cause)) {
		return false;
	}
	return (
		(typeof error.cause.name === 'string' &&
			KNOWN_NETWORK_ERRORS_TYPES.includes(error.cause.name)) ||
		(typeof error.cause.message === 'string' && isClientFetchErrorMessage(error.cause.message))
	);
};

/**
 * Determines if an error is an auth or permission error (401/403).
 * These errors are caused by the user not being authenticated or not having
 * permission to use the feature (e.g. IP allowlist, HIPAA, product not licensed).
 * They should be excluded from SLO failure counts as they are not service reliability issues.
 */
export const isAuthOrPermissionError = (error: unknown): boolean => {
	if (!isRecord(error)) {
		return false;
	}
	// RovoInstrumentedError shape
	const errorAttributes = isRecord(error.errorAttributes) ? error.errorAttributes : undefined;
	const statusFromAttributes = errorAttributes?.statusCode;
	// Direct status on error
	const attributes = isRecord(error.attributes) ? error.attributes : undefined;
	const statusDirect = error.status ?? attributes?.status;
	const status = statusFromAttributes ?? statusDirect;
	return status === 401 || status === 403;
};

/**
 * Determines if an error is a "product not active" or "access denied" error.
 * These occur when Rovo is not enabled for the user's org/product, predominantly
 * in Jira. They should be excluded from SLO failure counts.
 *
 * Uses the structured errorCode from ApiError.errorAttributes rather than
 * string matching on the human-readable message field.
 */
export const isProductNotActiveError = (error: unknown): boolean => {
	if (!isRecord(error)) {
		return false;
	}
	const errorAttributes = isRecord(error.errorAttributes) ? error.errorAttributes : undefined;
	const errorCode =
		typeof errorAttributes?.errorCode === 'string' ? errorAttributes.errorCode.toLowerCase() : '';
	return errorCode.includes('product.not.active') || errorCode.includes('product_not_active');
};
